import { useEffect, useState } from "react";
import { CrudPanel } from "../components/CrudPanel";
import { agentService } from "../services/agentService";
import { propertyService } from "../services/propertyService";
import { UserCheck } from "lucide-react";
import { mobileError, sanitizeMobile, MOBILE_LENGTH } from "../utils/validation";

const emptyAgent = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  govtId: "",
  company: "",
  username: "",
  password: "",
  tenantId: null,
  propertyUuid: "",
};

export function Agent({ notify, setHeaderAction, tenantId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyAgent);
  const [showForm, setShowForm] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const formFields = ["firstName", "lastName", "mobile", "email", "govtId", "company", "username", "password", "propertyUuid", "isDeleted"];

  useEffect(() => {
    propertyService.listForTenant(tenantId)
      .then((list) => setPropertyOptions(list.map((p) => ({ value: p.uuid, label: p.name }))))
      .catch(() => setPropertyOptions([]));
  }, [tenantId]);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction(
        <button
          className={showForm ? "secondary" : "primary"}
          onClick={() => setShowForm(!showForm)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {showForm ? "Hide" : "Add"} Agent
        </button>
      );
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [showForm, setHeaderAction]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await agentService.list(tenantId);
      setRows(result);
    } catch (err) {
      notify(err?.message || "Failed to load agents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    const mobileErr = mobileError(form.mobile);
    if (mobileErr) {
      setErrors({ mobile: mobileErr });
      notify(mobileErr);
      return;
    }
    setSaving(true);
    try {
      await agentService.save({ ...form, tenantId, propertyId: form.propertyUuid || null });
      setForm(emptyAgent);
      setShowForm(false);
      setErrors({});
      notify("Agent saved.");
      load();
    } catch (err) {
      console.error("Agent save error:", err);
      notify(err?.message || "Failed to save agent.");
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(key, value) {
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: undefined }));
    }
    if (key === "mobile" && value) {
      setForm((prev) => ({ ...prev, username: value }));
    }
  }

  async function remove(uuid) {
    if (!window.confirm("Delete this agent?")) return;
    try {
      await agentService.remove(uuid);
      notify("Agent deleted.");
      load();
    } catch (err) {
      console.error("Agent delete error:", err);
      notify(err?.message || "Failed to delete agent.");
    }
  }

  async function restore(uuid) {
    try {
      await agentService.restore(uuid);
      notify("Agent restored.");
      load();
    } catch (err) {
      console.error("Agent restore error:", err);
      notify(err?.message || "Failed to restore agent.");
    }
  }

  return (
    <CrudPanel
      title="Agent"
      sectionLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><UserCheck size={18} />Agent Management</span>}
      header="Agent List"
      rows={rows}
      loading={loading}
      saving={saving}
      form={form}
      setForm={setForm}
      emptyForm={emptyAgent}
      save={save}
      remove={remove}
      fields={formFields}
      firstRowFields={["firstName", "lastName", "mobile"]}
      hideTableFields={["password", "tenantId", "propertyId", "propertyUuid"]}
        hideFormFields={["isDeleted"]}
      passwordFields={["password"]}
      requiredFields={["firstName", "lastName", "mobile", "email", "govtId", "username", ...(form.uuid ? [] : ["password"])]}
      selectFields={{ propertyUuid: propertyOptions }}
      fieldLabels={{ propertyUuid: "Property", tenantId: "Tenant" }}
      telFields={["mobile"]}
      sanitizeFields={{ mobile: sanitizeMobile }}
      onFieldChange={handleFieldChange}
      onRestore={restore}
      collapsible={true}
      showForm={showForm}
      setShowForm={setShowForm}
      buttonLabel="Agent"
    />
  );
}